import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTasks } from '@/hooks/useTasks';
import { useAuth } from '@/hooks/useAuth';
import { useUsers } from '@/hooks/useUsers';
import { useWorkflowsByDepartmentId } from '@/hooks/useWorkflows';
import { useLeadDepartment, useDepartmentById } from '@/hooks/useConfig';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Task, TaskType, TaskPriority } from '@/lib/types';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Plus, Search, AlertCircle, Paperclip, X } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api';
import { useRef } from 'react';

const TASK_TYPES: TaskType[] = ['research', 'competitive_analysis', 'client_follow_up', 'reporting', 'other'];
const TASK_PRIORITIES: TaskPriority[] = ['low', 'medium', 'high'];

function taskTypeLabel(t: TaskType): string {
  return t.replace(/_/g, ' ');
}

export function TasksListPage() {
  const { isTeamLeader } = useAuth();
  const { users } = useUsers();
  const { leadDepartmentId } = useLeadDepartment();
  const { data: departmentWorkflows = [], isLoading: isDepartmentWorkflowsLoading } = useWorkflowsByDepartmentId(leadDepartmentId ?? undefined);
  const { data: leadDepartment } = useDepartmentById(leadDepartmentId ?? undefined);
  const { tasks, isLoading, createTask } = useTasks();

  const departmentHasWorkflow = isTeamLeader && leadDepartmentId && departmentWorkflows.length > 0;
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [pendingAttachmentIds, setPendingAttachmentIds] = useState<string[]>([]);
  const [pendingFiles, setPendingFiles] = useState<Array<{ file: File; attachmentId?: string; name: string }>>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    type: 'other' as TaskType,
    priority: 'medium' as TaskPriority,
    assigneesIds: [] as string[],
    startDate: '',
    dueDate: '',
  });

  const departmentMemberIds = new Set<string>();
  if (leadDepartment) {
    (leadDepartment.agents ?? []).forEach((a) => departmentMemberIds.add((a as { id: string }).id));
    const leaders = leadDepartment.teamLeaders ?? (leadDepartment.teamLeader ? [leadDepartment.teamLeader] : []);
    leaders.forEach((l) => {
      const id = (l as { user?: { id: string }; id?: string }).user?.id ?? (l as { id: string }).id;
      if (id) departmentMemberIds.add(id);
    });
  }
  const assigneeOptions = users.filter(
    (u) => departmentMemberIds.has(u.id) || (u.user_id && departmentMemberIds.has(u.user_id))
  );

  const filteredTasks = tasks.filter(
    (t) =>
      t.title.toLowerCase().includes(search.toLowerCase()) ||
      (t.description && t.description.toLowerCase().includes(search.toLowerCase()))
  );

  const handleCreate = () => {
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!form.description.trim() || form.description.trim().length < 10) {
      toast.error('Description must be at least 10 characters');
      return;
    }
    const startIso = form.startDate
      ? new Date(`${form.startDate}T00:00:00`).toISOString()
      : undefined;
    const dueIso = form.dueDate
      ? new Date(`${form.dueDate}T23:59:59.999`).toISOString()
      : undefined;
    if (startIso && dueIso && new Date(dueIso) <= new Date(startIso)) {
      toast.error('Due date must be after start date');
      return;
    }

    createTask.mutate(
      {
        title: form.title.trim(),
        description: form.description.trim(),
        type: form.type,
        priority: form.priority,
        assigneesIds: form.assigneesIds.length ? form.assigneesIds : undefined,
        startDate: startIso,
        dueDate: dueIso ?? undefined,
        attachments: pendingAttachmentIds.length ? pendingAttachmentIds : undefined,
      },
      {
        onSuccess: () => {
          setForm({ title: '', description: '', type: 'other', priority: 'medium', assigneesIds: [], startDate: '', dueDate: '' });
          setPendingAttachmentIds([]);
          setPendingFiles([]);
          setCreateOpen(false);
        },
      }
    );
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    e.target.value = '';
    setUploading(true);
    const ids: string[] = [];
    const pending: Array<{ file: File; attachmentId?: string; name: string }> = [];
    for (const file of files) {
      try {
        const prepare = await apiClient.prepareUpload({
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          uploadedByRequester: false,
        });
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', prepare.uploadUrl, true);
        Object.entries(prepare.headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
        await new Promise<void>((resolve, reject) => {
          xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error('Upload failed')));
          xhr.onerror = () => reject(new Error('Network error'));
          xhr.send(file);
        });
        const verified = await apiClient.verifyUpload(prepare.attachmentId);
        if (verified) {
          ids.push(prepare.attachmentId);
          pending.push({ file, attachmentId: prepare.attachmentId, name: file.name });
        }
      } catch (err) {
        console.error(err);
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setUploading(false);
    setPendingAttachmentIds((prev) => [...prev, ...ids]);
    setPendingFiles((prev) => [...prev, ...pending]);
  };

  const removePendingAttachment = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
    setPendingAttachmentIds((prev) => prev.filter((_, i) => i !== index));
  };

  if (!isTeamLeader) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Tasks</h1>
        <p className="text-muted-foreground">You see only tasks assigned to you.</p>
        {isLoading ? (
          <div className="flex justify-center py-12">Loading tasks...</div>
        ) : (
          <TasksTable tasks={filteredTasks} />
        )}
      </div>
    );
  }

  const toggleAssignee = (userId: string) => {
    setForm((prev) => ({
      ...prev,
      assigneesIds: prev.assigneesIds.includes(userId)
        ? prev.assigneesIds.filter((x) => x !== userId)
        : [...prev.assigneesIds, userId],
    }));
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Tasks</h1>
          <p className="text-muted-foreground">Department tasks and assignments</p>
        </div>
        {!isDepartmentWorkflowsLoading && !departmentHasWorkflow && isTeamLeader && leadDepartmentId && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Your department has no workflow assigned. You cannot create tasks until an admin assigns a workflow to your department in <strong>Settings → Workflows</strong> (edit a workflow and add your department).
            </AlertDescription>
          </Alert>
        )}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button disabled={isTeamLeader && !!leadDepartmentId && !departmentHasWorkflow}>
              <Plus className="h-4 w-4 mr-2" />
              Create task
            </Button>
          </DialogTrigger>
          <DialogContent className="flex max-h-[90vh] max-w-lg flex-col gap-0 overflow-hidden p-0">
            <DialogHeader className="shrink-0 space-y-1.5 px-6 pb-2 pt-6 pr-12 text-left sm:text-left">
              <DialogTitle>Create task</DialogTitle>
              <DialogDescription>Create a new task for {leadDepartment?.name ?? 'your department'}.</DialogDescription>
            </DialogHeader>
            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-2">
              <div className="space-y-4 pb-2">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleFileSelect}
                disabled={uploading}
              />
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                  placeholder="Task title"
                  maxLength={100}
                />
              </div>
              <div className="space-y-2">
                <Label>Description * (min 10 characters)</Label>
                <textarea
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  placeholder="Describe the task..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm((p) => ({ ...p, type: v as TaskType }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TASK_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>
                          {taskTypeLabel(t)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm((p) => ({ ...p, priority: v as TaskPriority }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TASK_PRIORITIES.map((p) => (
                        <SelectItem key={p} value={p}>
                          {p}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start date</Label>
                  <Input
                    type="date"
                    value={form.startDate}
                    onChange={(e) => setForm((p) => ({ ...p, startDate: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Due date</Label>
                  <Input
                    type="date"
                    value={form.dueDate}
                    onChange={(e) => setForm((p) => ({ ...p, dueDate: e.target.value }))}
                  />
                </div>
              </div>
              {assigneeOptions.length > 0 && (
                <div className="space-y-2">
                  <Label>Assign to (agents in department)</Label>
                  <div className="flex flex-wrap gap-2 border rounded-md p-2">
                    {assigneeOptions.map((u) => {
                      const userId = u.user_id ?? u.id;
                      const checked = form.assigneesIds.includes(userId);
                      return (
                        <label key={userId} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleAssignee(userId)}
                            className="rounded border-input"
                          />
                          {u.name}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label>Attachments</Label>
                {pendingFiles.length > 0 && (
                  <ul className="flex flex-wrap gap-2 text-sm">
                    {pendingFiles.map((p, i) => (
                      <li key={i} className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1">
                        <Paperclip className="h-3 w-3 text-muted-foreground" />
                        <span className="truncate max-w-[180px]">{p.name}</span>
                        <button
                          type="button"
                          onClick={() => removePendingAttachment(i)}
                          className="text-muted-foreground hover:text-foreground"
                          aria-label="Remove attachment"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className='ms-2'
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading || createTask.isPending}
                >
                  <Paperclip className="h-4 w-4 mr-1" />
                  {uploading ? 'Uploading...' : 'Add attachments'}
                </Button>
              </div>
              </div>
            </div>
            <DialogFooter className="shrink-0 border-t px-6 py-4 sm:justify-end">
              <Button variant="outline" onClick={() => setCreateOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={createTask.isPending || uploading}>
                Create task
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search tasks..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex justify-center py-12 text-muted-foreground">Loading tasks...</div>
      ) : (
        <TasksTable tasks={filteredTasks} />
      )}
    </div>
  );
}

function TasksTable({ tasks }: { tasks: Task[] }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isTasksWorkspace = location.pathname === '/admin/kanban';
  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Stage</TableHead>
              <TableHead>Priority</TableHead>
              <TableHead>Task Type</TableHead>
              <TableHead>Assignees</TableHead>
              <TableHead>Due date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tasks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  No tasks found
                </TableCell>
              </TableRow>
            ) : (
              tasks.map((task) => (
                <TableRow
                  key={task.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(`/admin/tasks/${task.id}${isTasksWorkspace ? '?from=kanban&view=list' : ''}`)}
                >
                  <TableCell>
                    <span className="font-medium text-primary hover:underline">
                      {task.title}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span
                      className="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium border"
                      style={{ borderColor: task.stage?.color ?? '#ccc', backgroundColor: `${task.stage?.color ?? '#eee'}20` }}
                    >
                      {task.stage?.name ?? '—'}
                    </span>
                  </TableCell>
                  <TableCell className="capitalize">{task.priority}</TableCell>
                  <TableCell className="capitalize">{task.type?.replace(/_/g, ' ') ?? '—'}</TableCell>
                  <TableCell>
                    {(() => {
                      const assigneeNames = (task.assignees ?? [])
                        .map((a) => (a as { user?: { profile?: { name?: string } } }).user?.profile?.name ?? '—')
                        .filter(Boolean);
                      if (assigneeNames.length === 0) return '—';
                      return (
                        <div className="space-y-1 max-w-[220px]">
                          {assigneeNames.map((name, idx) => (
                            <div key={`${task.id}-assignee-${idx}`} className="truncate">
                              {name}
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {task.dueDate ? format(new Date(task.dueDate), 'MMM d, yyyy') : '—'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
